import type { TFunction } from "i18next";
import type { QuoteRejection } from "@/api.types.v2";
import { totalWeightKg, type WeightUnit } from "@/lib/unit-display";

type PackageUnits = { weightUnit: WeightUnit; dimensionUnit: "cm" | "in" };

/** Server measurements are metric. Display the customer's units without
 * interpreting a missing rate as a physical size/weight limit. */
export function packageRejectionMessage(
  rejection: Pick<QuoteRejection, "code" | "reason" | "packageIssue">,
  packages: readonly PackageUnits[],
  t: TFunction<"app">
): string {
  const issue = rejection.packageIssue;
  if (!issue)
    return rejection.code === "PACKAGE_WEIGHT_UNPRICED"
      ? t("createShipment.packageWeightUnpriced")
      : rejection.reason;
  const number = issue.packageNumber;
  if (issue.kind === "sizeAndWeight")
    return t("createShipment.packageSizeWeight", { number });
  const pkg = packages[number - 1];
  const unit =
    issue.kind === "weight"
      ? (pkg?.weightUnit ?? "kg")
      : (pkg?.dimensionUnit ?? "cm");
  const factor =
    issue.kind === "weight"
      ? totalWeightKg([{ weight: 1, weightUnit: pkg?.weightUnit ?? "kg" }])
      : unit === "in"
        ? 2.54
        : 1;
  const actual = Number((issue.actual / factor).toFixed(3));
  const limit = `${factor === 1 ? "" : "≈"}${Number((issue.limit / factor).toFixed(3))}`;
  const values = { number, actual, limit, unit };
  if (issue.kind === "weight")
    return t("createShipment.packageActualWeight", values);
  if (issue.kind === "longestSide")
    return t("createShipment.packageLongestSide", values);
  return t("createShipment.packageLengthGirth", values);
}
